import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';

import { sortShiftTypesIntoTree } from '@/api/shift-type-api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ShiftType } from '@/types/template';

/**
 * Hierarchical shift-type picker (WW7). Renders the flat list returned
 * by `useShiftTypes`, sorted into parent → child order via
 * `sortShiftTypesIntoTree`, with non-root entries indented by
 * `depth` and a `↳` marker so the hierarchy is visible in the menu.
 *
 * The menu's textContent is what Radix's Select reflects in the
 * trigger, so the selected child appears with its `↳ ` prefix once
 * picked — acceptable for v1 and unambiguous about hierarchy. If a
 * cleaner trigger label is wanted later, replace the SelectValue
 * with a custom render that reads only the selected type's `name`.
 */
export function ShiftTypeSelect({ shiftTypes, value, onChange }: ShiftTypeSelectProps) {
  const { t } = useLingui();
  const sorted = useMemo(() => sortShiftTypesIntoTree(shiftTypes), [shiftTypes]);

  function handleChange(id: string) {
    const match = sorted.find((st) => st.id === id);
    onChange(id, match?.name ?? id);
  }

  return (
    <Select value={value} onValueChange={handleChange}>
      <SelectTrigger aria-label={t`Shift type`}>
        <SelectValue placeholder={t`Select a shift type`} />
      </SelectTrigger>
      <SelectContent>
        {sorted.map((shiftType) => (
          <SelectItem key={shiftType.id} value={shiftType.id}>
            {indent(shiftType.depth)}
            {shiftType.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Build the indent prefix for a hierarchy row. Depth 0 -> empty.
 * Depth 1+ -> two U+00A0 (non-breaking space) characters per level
 * plus a marker so the relationship is visible without relying on
 * CSS indentation.
 *
 * Non-breaking spaces are required because the regular ASCII space
 * sequence would be collapsed to a single space under the default
 * `white-space: normal` CSS, defeating depth-based indentation
 * beyond level 1.
 */
function indent(depth: number): string {
  if (depth <= 0) return '';
  return `${'  '.repeat(depth)}↳ `;
}

interface ShiftTypeSelectProps {
  shiftTypes: ShiftType[];
  value: string;
  onChange: (id: string, name: string) => void;
}
